import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from './auth/services/auth.service';
import { ApplicationFrameComponent, FooterLink, SidebarItem, SpinnerComponent } from '@siemens/component-lib';
import { HeaderMenuComponent } from './shared/components/header-menu/header-menu.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppComponentOperations } from './app-component.operations';
import { Subject, takeUntil, tap } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { WorkflowDialogHandlerComponent } from './workflows/create-workflow/create-workflow-handler/create-workflow-handler.component';
import { ModuleRegistry } from 'ag-grid-enterprise';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { environment } from '../environments/environment';

ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseManager.setLicenseKey(environment.agGridEnterpriseLicenseKey);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterOutlet,
        SpinnerComponent,
        ApplicationFrameComponent,
        HeaderMenuComponent,
        TranslatePipe,
        WorkflowDialogHandlerComponent
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    protected router = inject(Router);
    protected authService = inject(AuthService);
    private translateService = inject(TranslateService);
    private pageTitleService = inject(Title);
    protected readonly stageLabel = this.resolveStageLabel();

    protected sidebarItems = signal<SidebarItem[]>([]);
    protected bottomSidebarItems = signal<SidebarItem[]>([]);
    protected footerLinks = signal<FooterLink[]>([]);
    protected workflowDialogHandler = viewChild(WorkflowDialogHandlerComponent);
    private destroy$: Subject<boolean> = new Subject<boolean>();

    ngOnInit() {
        this.subscribeToLanguageChanges();
        this.updateTranslations();
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected isRouterPathUnauthorized(): boolean {
        return this.router.url === '/unauthorized';
    }

    protected openCreateWorkflowDialog(): void {
        if (this.router.url.includes('workflows/details')) {
            void this.router.navigate(['/']);
        }
        this.workflowDialogHandler()?.openCreateWorkflowDialog();
    }

    private updateApplicationFrameTranslations(): void {
        const { sidebarItems, footerLinks, bottomSidebarItems } =
            AppComponentOperations.getInitializedApplicationFrameTranslations(this.translateService);
        this.sidebarItems.set(sidebarItems);
        this.footerLinks.set(footerLinks);
        this.bottomSidebarItems.set(bottomSidebarItems);
    }

    private updatePageTitleTranslation(): void {
        this.pageTitleService.setTitle(this.translateService.instant('page-titles.home'));
    }

    private subscribeToLanguageChanges(): void {
        this.translateService.onLangChange
            .pipe(
                tap(() => this.updateTranslations()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private updateTranslations(): void {
        this.updateApplicationFrameTranslations();
        this.updatePageTitleTranslation();
    }

    private resolveStageLabel(): string | null {
        const apiBaseUrl = environment.apiConfig[0]?.uri?.toLowerCase() ?? '';

        if (apiBaseUrl.includes('.int.') || apiBaseUrl.includes('/int/')) {
            return 'INT';
        }

        if (apiBaseUrl.includes('.dev.') || apiBaseUrl.includes('/dev/')) {
            return 'DEV';
        }

        return null;
    }
}
